import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SelectBusca } from "@/components/padroes";
import { api, ApiError } from "@/lib/api";
import type { User, Tomador, Departamento } from "@/data/mock";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

const MAX_NOME = 150;
const SEM_TOMADOR = "__none__";
const SEM_DEPARTAMENTO = "__none__";

interface Props {
  open: boolean;
  onClose: () => void;
  onSuccess: (usuario: User) => void;
  tomadores: Tomador[];
}

export const AdicionarUsuarioModal = ({ open, onClose, onSuccess, tomadores }: Props) => {
  const [nome, setNome] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<"colaborador" | "rh" | "financeiro">("colaborador");
  const [department, setDepartment] = useState(SEM_DEPARTAMENTO);
  const [tomadorId, setTomadorId] = useState(SEM_TOMADOR);
  const [saving, setSaving] = useState(false);
  const [departamentos, setDepartamentos] = useState<Departamento[]>([]);

  useEffect(() => {
    if (!open) return;
    api.get<Departamento[]>("/departamentos").then(setDepartamentos).catch(() => setDepartamentos([]));
  }, [open]);

  const reset = () => {
    setNome("");
    setEmail("");
    setRole("colaborador");
    setDepartment(SEM_DEPARTAMENTO);
    setTomadorId(SEM_TOMADOR);
  };

  const handleClose = () => {
    if (saving) return;
    reset();
    onClose();
  };

  const handleSalvar = async () => {
    const trimmedNome = nome.trim();
    const trimmedEmail = email.trim();

    if (!trimmedNome) {
      toast.error("Nome não pode ser vazio");
      return;
    }
    if (trimmedNome.length > MAX_NOME) {
      toast.error(`Nome deve ter no máximo ${MAX_NOME} caracteres`);
      return;
    }
    if (!trimmedEmail) {
      toast.error("Email não pode ser vazio");
      return;
    }

    setSaving(true);
    try {
      const novoUsuario = await api.post<User>("/colaboradores", {
        name: trimmedNome,
        email: trimmedEmail,
        role,
        department: department === SEM_DEPARTAMENTO ? undefined : department,
        tomadorId: tomadorId === SEM_TOMADOR ? undefined : tomadorId,
      });
      toast.success("Usuário adicionado com sucesso");
      onSuccess(novoUsuario);
      reset();
      onClose();
    } catch (err) {
      if (err instanceof ApiError && err.status === 400 && err.body && (err.body as { error?: { code?: string } }).error?.code === "EMAIL_EM_USO") {
        toast.error("Já existe um usuário com este email");
      } else if (err instanceof ApiError && err.status === 404) {
        toast.error("Tomador não encontrado");
      } else if (err instanceof ApiError && err.status === 403) {
        toast.error("Acesso negado");
      } else {
        toast.error(err instanceof Error ? err.message : "Erro ao adicionar usuário");
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Adicionar usuário</DialogTitle>
          <DialogDescription>Login segue via Google — o email precisa bater.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="novo-usuario-nome">Nome completo</Label>
            <Input
              id="novo-usuario-nome"
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              maxLength={MAX_NOME}
              autoFocus
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="novo-usuario-email">Email</Label>
            <Input
              id="novo-usuario-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="novo-usuario-role">Perfil</Label>
            <Select value={role} onValueChange={(v) => setRole(v as typeof role)}>
              <SelectTrigger id="novo-usuario-role">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="colaborador">Colaborador</SelectItem>
                <SelectItem value="rh">RH</SelectItem>
                <SelectItem value="financeiro">Financeiro</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="novo-usuario-departamento">Departamento (opcional)</Label>
            <SelectBusca
              id="novo-usuario-departamento"
              value={department}
              onChange={setDepartment}
              placeholder="Sem departamento"
              placeholderBusca="Buscar departamento..."
              opcoes={[
                { value: SEM_DEPARTAMENTO, label: "Sem departamento", neutra: true },
                ...departamentos.map((d) => ({ value: d.name, label: d.name })),
              ]}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="novo-usuario-tomador">Tomador vinculado (opcional)</Label>
            <SelectBusca
              id="novo-usuario-tomador"
              value={tomadorId}
              onChange={setTomadorId}
              placeholder="Sem tomador"
              placeholderBusca="Buscar tomador..."
              opcoes={[
                { value: SEM_TOMADOR, label: "Sem tomador", neutra: true },
                ...tomadores.map((t) => ({ value: t.id, label: t.razaoSocial })),
              ]}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={saving}>Cancelar</Button>
          <Button onClick={handleSalvar} disabled={saving}>
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Adicionar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
